import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FileMergeModal from './FileMergeModal';
import SelectedFilesModal from './SelectedFilesModal';
import MergeProgressNotification from './MergeProgressNotification';
import { useDispatch, useSelector } from 'react-redux';
import { appendAIPaneFromCurrentUrl } from '../../utils/aiPaneUrl';
import {
  handleFileSelected as handleFileSelectedUtil,
  handleAddFiles as handleAddFilesUtil,
  handleRemoveFile as handleRemoveFileUtil,
  handleReorder as handleReorderUtil,
  handleCombineFiles as handleCombineFilesUtil,
  handleCloseMergeNotification as handleCloseMergeNotificationUtil,
  handleCancel as handleCancelUtil,
  handleClose as handleCloseUtil,
} from './FileMergeModalContainerUtils';
import { postHighlightedMatter } from '../../slice/MatterDetailsSlice';


const FileMergeModalContainer = ({
  open,
  onClose,
  treeData = [],
  onCombineFiles,
  selectedFile,
  setSelectedFile,
  // Pre-seeded file records (table multi-select "Merge selected"): skips
  // the tree picker and opens the reorder/confirm step directly.
  initialFiles = null,
  // Current document-list view context from the OWNER of that state
  // (TabView keeps page/sort in local state — the redux slice only tracks
  // docListingPage and no sort at all, per Bugbot #236). Shape:
  // { page, sort_direction, sort_by }.
  listContext = null,
  matterId,
  navigate,
  ...fileMergeModalProps
}) => {
  const [showFileMergeModal, setShowFileMergeModal] = useState(open);
  const [showSelectedFilesModal, setShowSelectedFilesModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [outputName, setOutputName] = useState('');
  const [mergeProgress, setMergeProgress] = useState(0);
  const [isMerging, setIsMerging] = useState(false);
  const [isMergeComplete, setIsMergeComplete] = useState(false);
  const [mergedFileUrl, setMergedFileUrl] = useState(null);
  const [mergedFile, setMergedFile] = useState(null);
  const [mergeError, setMergeError] = useState(null);
  const dispatch = useDispatch();
  const { matterDetails, matterDetailsTree, docListingPage } = useSelector(
    (state) => state.matterDetails
  );
  const currentFolderId = useSelector(
    (state) => state.websocket?.currentFolderId
  );
  // Post split-schema, matter serializers no longer emit `sf_id`; `id` IS
  // the Salesforce text id the backend's multi-doc merge endpoint resolves.
  // Prefer the tree's id — matterDetails.id becomes a FOLDER id while the
  // user is browsing inside a folder (fetchMatterDetailsFolder overwrites).
  const currentMatterId =
    matterDetailsTree?.id || matterDetails?.id || matterDetails?.sf_id;
  // Wrapper functions that call utils with state setters
  const handleFileSelected = (file) => {
    handleFileSelectedUtil(
      file,
      setSelectedFiles,
      setShowFileMergeModal,
      setShowSelectedFilesModal,
      setSelectedFile
    );
  };

  const handleAddFiles = () => {
    handleAddFilesUtil(setShowSelectedFilesModal, setShowFileMergeModal);
  };

  const handleRemoveFile = (index) => {
    handleRemoveFileUtil(index, setSelectedFiles);
  };

  const handleReorder = (newOrder) => {
    handleReorderUtil(newOrder, setSelectedFiles);
  };

  const handleCombineFiles = async () => {
    // The folder the user is browsing (websocket.currentFolderId is the
    // bare id from the URL; both the list API and the assembly API take
    // the F-prefixed form). Used as the merge output's destination AND
    // the post-merge list-refresh scope, so the new file appears exactly
    // where the user is looking.
    const scopedFolder =
      currentFolderId && currentFolderId !== 'root'
        ? String(currentFolderId).startsWith('F')
          ? String(currentFolderId)
          : `F${currentFolderId}`
        : undefined;
    await handleCombineFilesUtil({
      selectedFiles,
      currentMatterId,
      outputName,
      destinationFolder: scopedFolder,
      listRefreshArgs: {
        page: listContext?.page || docListingPage || 1,
        sort_direction: listContext?.sort_direction,
        sort_by: listContext?.sort_by,
        folder: scopedFolder,
      },
      setMergeProgress,
      setIsMerging,
      setIsMergeComplete,
      setMergedFileUrl,
      setMergedFile,
      setMergeError,
      setShowFileMergeModal,
      setShowSelectedFilesModal,
      setSelectedFile,
      setSelectedFiles,
      onClose,
      dispatch
  });
     
  };

  const handleOpenMergedFile = () => {
    setIsMerging(false);
    setIsMergeComplete(false);
    setMergedFileUrl(null);
    setMergeProgress(0);
    // Build payload with tab_action for documents
    const userId = Number(localStorage.getItem("user_id"));
    const highlightPayload = {
      matterId: mergedFile.matter,
      docId: mergedFile.unique_id.substring(1),
      docType: "document",
      tabAction: {
        usr_id: userId,
        document_id: String(mergedFile.unique_id.substring(1)),
        matter_id: String(mergedFile.matter),
        action: "open"
      }
    };
    dispatch(postHighlightedMatter(highlightPayload)).then((res)=>{
           navigate(appendAIPaneFromCurrentUrl(`/matter${mergedFileUrl}`))
          })
    
  };

  const handleCloseMergeNotification = () => {
    handleCloseMergeNotificationUtil(
      setIsMerging,
      setIsMergeComplete,
      setMergedFileUrl,
      setMergeProgress,
      setMergeError
    );
  };

  const handleCancel = () => {
    handleCancelUtil(() => handleClose());
  };

  const handleClose = () => {
    handleCloseUtil(
      setShowFileMergeModal,
      setShowSelectedFilesModal,
      setSelectedFile,
      setSelectedFiles,
      onClose
    );
  };

  // Handle selectedFile prop change
  useEffect(() => {
    if (selectedFile) {
      handleFileSelected({ id: selectedFile?.unique_id, record: selectedFile });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);

  // Update modals when open prop changes. When rows were pre-selected in
  // the table ("Merge selected"), seed them and jump straight to the
  // reorder/confirm step — the tree picker is only for adding more.
  React.useEffect(() => {
    if (open) {
      setOutputName('');
      // A previous run's failure/completion must not bleed into a fresh
      // merge (Bugbot #236) — but leave isMerging alone: an in-flight
      // poll owns that flag and its notification.
      setMergeError(null);
      setIsMergeComplete(false);
      setMergedFileUrl(null);
      if (Array.isArray(initialFiles) && initialFiles.length > 0) {
        setSelectedFiles(
          initialFiles.map((record) => ({
            ...record,
            id: record.unique_id || record.id || crypto.randomUUID(),
          }))
        );
        setShowFileMergeModal(false);
        setShowSelectedFilesModal(true);
      } else {
        setShowFileMergeModal(true);
        setShowSelectedFilesModal(false);
      }
    } else {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFiles]);

  return (
    <>
      <FileMergeModal
        {...fileMergeModalProps}
        open={showFileMergeModal}
        onClose={handleClose}
        treeData={treeData}
        onProceed={handleFileSelected}
        onCancel={handleClose}
        selectedFiles={selectedFiles}
      />

      <SelectedFilesModal
        open={showSelectedFilesModal}
        onClose={handleClose}
        selectedFiles={selectedFiles}
        onRemoveFile={handleRemoveFile}
        onAddFiles={handleAddFiles}
        onCombineFiles={handleCombineFiles}
        onCancel={handleCancel}
        onReorder={handleReorder}
        outputName={outputName}
        onOutputNameChange={setOutputName}
      />

      {(isMerging || isMergeComplete || mergeError) && (
        <MergeProgressNotification
          progress={mergeProgress}
          isComplete={isMergeComplete}
          mergedFileUrl={mergedFileUrl}
          error={mergeError}
          onClose={handleCloseMergeNotification}
          onOpenFile={handleOpenMergedFile}
        />
      )}
    </>
  );
};

FileMergeModalContainer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  treeData: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      children: PropTypes.array,
    })
  ),
  onCombineFiles: PropTypes.func,
  selectedFile: PropTypes.object,
  setSelectedFile: PropTypes.func,
  initialFiles: PropTypes.array,
  listContext: PropTypes.shape({
    page: PropTypes.number,
    sort_direction: PropTypes.string,
    sort_by: PropTypes.string,
  }),
  matterId: PropTypes.string,
  navigate:PropTypes.func
};

export default FileMergeModalContainer;

